/**
 * Registro dos tours guiados de onboarding.
 *
 * Rodada 1: Dashboard. Rodada 2 (2026-08-06): Clientes, Processos, Tarefas
 * e Agenda — o núcleo operacional do dia a dia.
 *
 * Cada passo aponta para um elemento marcado com `data-tour="<target>"` na
 * tela. `route` é o prefixo de rota onde o tour deve rodar — usado pelo
 * `TourProvider` para saber quando a navegação terminou e é seguro
 * consultar os seletores no DOM. Importante: todos os passos de um tour
 * precisam existir simultaneamente na MESMA tela (o motor do tour não
 * navega entre rotas nem abre modais sozinho) — por isso os passos ficam
 * restritos à tela principal de cada módulo (a mesma que o link da
 * sidebar abre), nunca a um formulário/detalhe em outra rota ou a um
 * modal que só existe depois de um clique do usuário.
 */
import type { TourKey } from '@proadv/types';

export interface TourStep {
  /** Valor do atributo `data-tour` do elemento alvo (sem o seletor `[data-tour="..."]`). */
  target: string;
  title: string;
  body: string;
}

export interface TourDefinition {
  key: TourKey;
  /** Nome amigavel do modulo, usado na sidebar (title do indicador) e na
   * lista de "Configuracoes > Tour do sistema". */
  label: string;
  /** Prefixo de rota onde o tour roda (ex: '/escritorio'). */
  route: string;
  steps: TourStep[];
}

export const TOURS: Record<TourKey, TourDefinition> = {
  dashboard: {
    key: 'dashboard',
    label: 'Dashboard',
    route: '/escritorio',
    steps: [
      {
        target: 'dashboard-greeting',
        title: 'Bem-vindo ao seu Dashboard',
        body: 'Aqui você acompanha, num único lugar, o resumo do seu escritório: processos, clientes, prazos e financeiro.',
      },
      {
        target: 'dashboard-period',
        title: 'Filtro de período e escopo',
        body: 'Troque entre hoje, semana, mês ou ano, e entre visão pessoal ou do time inteiro.',
      },
      {
        target: 'dashboard-widgets',
        title: 'Widgets do painel',
        body: 'Cada cartão traz um indicador do seu escritório, atualizado automaticamente.',
      },
      {
        target: 'dashboard-edit-layout',
        title: 'Personalize o painel',
        body: 'Clique aqui para entrar no modo de edição, adicionar, remover ou reorganizar widgets do jeito que preferir.',
      },
      {
        target: 'topbar-search',
        title: 'Busca global',
        body: 'Encontre clientes, processos, tarefas e documentos rapidamente. Atalho de teclado: Ctrl+K (ou Cmd+K no Mac).',
      },
      {
        target: 'topbar-notifications',
        title: 'Notificações',
        body: 'Fique de olho em intimações novas, prazos e atualizações importantes direto por aqui.',
      },
    ],
  },

  clientes: {
    key: 'clientes',
    label: 'Clientes',
    route: '/escritorio/clientes',
    steps: [
      {
        target: 'clientes-novo-contato',
        title: 'Cadastre um novo cliente',
        body: 'Clique aqui para começar um cadastro. Você escolhe entre Pessoa física ou Pessoa jurídica logo no início do formulário.',
      },
      {
        target: 'clientes-busca',
        title: 'Busca rápida',
        body: 'Encontre um cliente pelo nome ou pelo CPF/CNPJ sem precisar rolar a lista.',
      },
      {
        target: 'clientes-filtrar',
        title: 'Filtre por status e origem',
        body: 'Combine status (lead, ativo, inativo etc) e origem de captação para refinar a lista.',
      },
      {
        target: 'clientes-ordenar',
        title: 'Ordene a lista',
        body: 'Troque entre mais recentes, mais antigos ou ordem alfabética pelo nome.',
      },
      {
        target: 'clientes-tabela',
        title: 'Detalhe completo do cliente',
        body: 'Clique em qualquer linha para abrir o cadastro completo: dados, contatos, endereço, processos vinculados, financeiro, documentos, anotações e anexos.',
      },
    ],
  },

  processos: {
    key: 'processos',
    label: 'Processos',
    route: '/escritorio/processos',
    steps: [
      {
        target: 'processos-novo',
        title: 'Cadastre um novo processo',
        body: 'Informe o número CNJ e clique em Buscar no CNJ: nós preenchemos automaticamente partes, tribunal, vara e movimentações via DataJud.',
      },
      {
        target: 'processos-stats-pills',
        title: 'Panorama por status',
        body: 'Acompanhe rapidamente quantos processos estão ativos, suspensos, arquivados ou encerrados.',
      },
      {
        target: 'processos-busca',
        title: 'Busca rápida',
        body: 'Encontre um processo pelo número CNJ ou pelo nome de uma das partes.',
      },
      {
        target: 'processos-filtrar',
        title: 'Filtros avançados',
        body: 'Refine por status, grupo e tipo de ação, natureza, tribunal ou vara.',
      },
      {
        target: 'processos-tabela',
        title: 'Detalhe do processo',
        body: 'Clique em qualquer linha para abrir o processo completo, com as partes envolvidas e o histórico de movimentações.',
      },
    ],
  },

  tarefas: {
    key: 'tarefas',
    label: 'Atividades',
    route: '/escritorio/atividades',
    steps: [
      {
        target: 'atividades-nova-tarefa',
        title: 'Crie uma nova tarefa',
        body: 'Defina categoria, um ou mais responsáveis, data de compromisso e, se houver, o prazo fatal.',
      },
      {
        target: 'atividades-busca',
        title: 'Busca rápida',
        body: 'Encontre uma tarefa pelo título ou pela descrição.',
      },
      {
        target: 'atividades-filtro-status',
        title: 'Filtre por status',
        body: 'Veja só as tarefas pendentes, concluídas ou atrasadas.',
      },
      {
        target: 'atividades-filtro-prioridade',
        title: 'Filtre por prioridade',
        body: 'Separe o que é urgente do que pode esperar.',
      },
      {
        target: 'atividades-filtro-responsavel',
        title: 'Filtre por responsável',
        body: 'Use "Atribuídas a mim" para ver rapidamente só as suas tarefas.',
      },
      {
        target: 'atividades-tabela',
        title: 'Sua lista de tarefas',
        body: 'Cada linha mostra partes vinculadas, compromisso, prazo fatal e responsáveis. Marque como concluída direto por aqui.',
      },
    ],
  },

  agenda: {
    key: 'agenda',
    label: 'Agenda',
    route: '/escritorio/agenda',
    steps: [
      {
        target: 'agenda-navegacao-periodo',
        title: 'Navegue pelo calendário',
        body: 'Use as setas para andar entre os períodos anterior e seguinte.',
      },
      {
        target: 'agenda-view-switch',
        title: 'Troque a visão',
        body: 'Veja o calendário por mês, semana ou dia, conforme o nível de detalhe que precisar.',
      },
      {
        target: 'agenda-adicionar',
        title: 'Adicione um compromisso',
        body: 'Crie uma tarefa com data de compromisso ou prazo fatal direto pela agenda.',
      },
      {
        target: 'agenda-filtro-responsaveis',
        title: 'Filtre por responsável',
        body: 'Marque quem você quer ver na agenda: só você ou o time inteiro.',
      },
      {
        target: 'agenda-filtro-tipo-data',
        title: 'Tipo de data',
        body: 'Escolha se o calendário mostra a data do compromisso ou o prazo fatal das tarefas.',
      },
      {
        target: 'agenda-filtro-status',
        title: 'Filtre por status',
        body: 'Combine pendentes, concluídas e atrasadas do jeito que fizer sentido pra você.',
      },
      {
        target: 'agenda-calendario',
        title: 'O calendário',
        body: 'Clique num horário vazio para criar um compromisso, ou num evento existente para abrir os detalhes.',
      },
    ],
  },

  financeiro: {
    key: 'financeiro',
    label: 'Financeiro',
    route: '/escritorio/financeiro',
    steps: [
      {
        target: 'financeiro-novo-lancamento',
        title: 'Registre um lançamento',
        body: 'Receita, despesa ou transferência entre contas. Dá pra configurar recorrência e parcelas direto no formulário.',
      },
      {
        target: 'financeiro-totais',
        title: 'Totais do período filtrado',
        body: 'Receita, despesa e saldo somados de acordo com os filtros ativos.',
      },
      {
        target: 'financeiro-busca',
        title: 'Busca rápida',
        body: 'Encontre um lançamento pela descrição, anotação ou nome do cliente.',
      },
      {
        target: 'financeiro-filtros',
        title: 'Filtre a lista',
        body: 'Combine tipo, status, conta bancária e categoria para refinar os lançamentos exibidos.',
      },
      {
        target: 'financeiro-periodo',
        title: 'Período e limpeza de filtros',
        body: 'Escolha a dimensão de data (vencimento, pagamento etc) e o intervalo. "Limpar filtros" reseta tudo de uma vez.',
      },
      {
        target: 'financeiro-tabela',
        title: 'Seus lançamentos',
        body: 'A data de pagamento pode ser editada direto na linha, sem abrir o lançamento completo.',
      },
    ],
  },

  documentos: {
    key: 'documentos',
    label: 'Documentos',
    route: '/escritorio/documentos',
    steps: [
      {
        target: 'documentos-abas',
        title: 'Modelos e documentos gerados',
        body: 'Alterne entre o catálogo de modelos e os documentos já gerados a partir deles.',
      },
      {
        target: 'documentos-busca',
        title: 'Busca rápida',
        body: 'Encontre um modelo pelo nome ou pela descrição.',
      },
      {
        target: 'documentos-filtro-categoria',
        title: 'Filtre por categoria',
        body: 'Organize o catálogo por categoria de modelo.',
      },
      {
        target: 'documentos-criar-em-branco',
        title: 'Crie um modelo do zero',
        body: 'Abre o editor de texto com suporte a variáveis, tipo [[nome]], que puxam dados do cliente ou processo na hora de gerar o documento.',
      },
      {
        target: 'documentos-grade',
        title: 'Catálogo de modelos',
        body: 'Clique nos três pontinhos de qualquer modelo para editar, gerar um documento, duplicar ou excluir.',
      },
    ],
  },

  relatorios: {
    key: 'relatorios',
    label: 'Relatórios',
    route: '/escritorio/relatorios',
    steps: [
      {
        target: 'relatorios-abas',
        title: 'Tipos, gerados, automações e mais',
        body: 'Além dos tipos de relatório, você acompanha os PDFs já gerados, automações agendadas e aprovações pendentes.',
      },
      {
        target: 'relatorios-busca',
        title: 'Busca rápida',
        body: 'Encontre um tipo de relatório pelo nome.',
      },
      {
        target: 'relatorios-instalar-prontos',
        title: 'Tipos prontos do sistema',
        body: 'Instale modelos de relatório já configurados em vez de montar um do zero.',
      },
      {
        target: 'relatorios-novo-tipo',
        title: 'Crie um tipo de relatório',
        body: 'Defina como os dados do escritório são compilados no PDF final.',
      },
      {
        target: 'relatorios-tabela',
        title: 'Gere o relatório',
        body: 'Clique em Gerar na linha de um tipo para escolher os parâmetros e produzir o PDF.',
      },
    ],
  },

  intimacoes: {
    key: 'intimacoes',
    label: 'Intimações',
    route: '/escritorio/intimacoes',
    steps: [
      {
        target: 'intimacoes-filtro-status',
        title: 'Não lidas, todas ou lidas',
        body: 'Quando configurados, você também filtra por escritório monitorado (OAB), termo monitorado ou tribunal, aqui do lado.',
      },
      {
        target: 'intimacoes-busca',
        title: 'Busca e atualização',
        body: 'Busque no texto da publicação ou atualize a lista manualmente.',
      },
      {
        target: 'intimacoes-lista',
        title: 'Publicações do DJEN',
        body: 'Cada linha é uma intimação recebida. Clique para abrir o texto completo.',
      },
      {
        target: 'intimacoes-detalhe',
        title: 'Detalhe da intimação',
        body: 'Ao abrir uma intimação você pode marcar como lida, vincular a um processo e definir um prazo manualmente, quando o sistema não gerar a tarefa sozinho.',
      },
    ],
  },

  configuracoes: {
    key: 'configuracoes',
    label: 'Configurações',
    route: '/escritorio/configuracoes',
    steps: [
      {
        target: 'config-sidebar',
        title: 'Tudo o que dá pra configurar',
        body: 'Dados do escritório, equipe, prazos, financeiro, modelos de documento, monitoramentos e muito mais, tudo organizado aqui.',
      },
      {
        target: 'config-dados-escritorio',
        title: 'Dados do escritório',
        body: 'Razão social, CNPJ, OAB responsável e identidade visual do sistema.',
      },
      {
        target: 'config-usuarios',
        title: 'Usuários',
        body: 'Convide a equipe e defina o papel de cada pessoa no escritório.',
      },
      {
        target: 'config-assinatura',
        title: 'Assinatura',
        body: 'Acompanhe seu plano atual e a forma de pagamento.',
      },
      {
        target: 'config-conteudo',
        title: 'Conteúdo da seção',
        body: 'O que você seleciona no menu ao lado aparece aqui.',
      },
    ],
  },
};

export function getTourDefinition(key: TourKey): TourDefinition {
  return TOURS[key];
}

/**
 * Ordem de exibicao da pagina "Configuracoes > Tour do sistema" — espelha a
 * ordem visual da sidebar (PRIMARY_ITEMS + Configuracoes por ultimo), que
 * NAO bate com a ordem de insercao de `TOURS` acima (lá Financeiro/
 * Documentos e Intimacoes estao fora de ordem). `Object.values(TOURS)`
 * pareceria "obvio" aqui mas produziria uma lista com Financeiro antes de
 * Documentos e Intimacoes quase no fim — por isso a lista explicita.
 */
const DISPLAY_ORDER: TourKey[] = [
  'dashboard',
  'clientes',
  'processos',
  'intimacoes',
  'tarefas',
  'agenda',
  'documentos',
  'financeiro',
  'relatorios',
  'configuracoes',
];

/** Todos os tours, na ordem de exibicao (mesma ordem da sidebar). Usado
 * pela pagina "Configuracoes > Tour do sistema". */
export const ALL_TOURS: TourDefinition[] = DISPLAY_ORDER.map((key) => TOURS[key]);
